import { useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, ChevronLeft, ChevronRight } from 'lucide-react';
import { ProductCard } from '../ProductCard/ProductCard';
import { useFeaturedProducts } from '../../services/productService';

const ITEMS_PER_PAGE = 4;

/**
 * Featured products section — Yelp-style vertical list of product cards
 * with numbered left-aligned pagination.
 */
export function FeaturedProducts() {
  const { data: products, isLoading, error } = useFeaturedProducts();
  const [page, setPage] = useState(1);

  if (error) {
    return (
      <section className="py-10 px-4">
        <p className="text-center text-error text-sm">Failed to load featured products.</p>
      </section>
    );
  }

  const totalPages = products ? Math.ceil(products.length / ITEMS_PER_PAGE) : 1;
  const paginatedProducts = products
    ? products.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
    : [];
  const visiblePages = getVisiblePages(page, totalPages);

  return (
    <section className="py-10 sm:py-14 bg-ui-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-2 mb-6"
        >
          <Flame size={26} className="text-brand-red" />
          <h2 className="text-xl sm:text-2xl font-extrabold text-brand-dark">
            Featured Menu
          </h2>
          {products?.length > 0 && (
            <span className="text-sm text-brand-gray ml-1">({products.length})</span>
          )}
        </motion.div>

        {/* Loading skeletons */}
        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="flex gap-4 p-4 bg-brand-white rounded-lg border border-ui-border animate-pulse">
                <div className="w-56 h-40 bg-brand-light rounded-lg flex-shrink-0 hidden sm:block" />
                <div className="flex-1 space-y-3">
                  <div className="h-5 bg-brand-light rounded w-2/3" />
                  <div className="h-3 bg-brand-light rounded w-24" />
                  <div className="h-3 bg-brand-light rounded w-full" />
                  <div className="h-3 bg-brand-light rounded w-3/4" />
                  <div className="h-8 bg-brand-light rounded w-36 mt-2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Product list — vertical stack of horizontal cards */}
        {!isLoading && paginatedProducts.length > 0 && (
          <div className="space-y-3">
            {paginatedProducts.map((product, i) => (
              <ProductCard
                key={product.id}
                product={product}
                index={(page - 1) * ITEMS_PER_PAGE + i + 1}
              />
            ))}
          </div>
        )}

        {/* Numbered Pagination — left-aligned */}
        {!isLoading && totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-start gap-1.5 mt-8"
          >
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-ui-border bg-brand-white hover:bg-brand-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} className="text-brand-dark" />
            </button>

            {visiblePages.map((p, i) =>
              p === '...' ? (
                <span key={`dot-${i}`} className="w-9 h-9 flex items-center justify-center text-sm text-brand-gray">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={
                    p === page
                      ? 'w-9 h-9 flex items-center justify-center rounded-lg text-sm font-bold bg-brand-red text-brand-white shadow-sm'
                      : 'w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-brand-dark border border-ui-border bg-brand-white hover:bg-brand-light transition-colors'
                  }
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-ui-border bg-brand-white hover:bg-brand-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={16} className="text-brand-dark" />
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}

/** Helper — page numbers with '...' for display. */
function getVisiblePages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}
